import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import Button from "../../components/Button/Button";
import Card from "../../components/Card/Card";
import Loader from "../../components/Loader/Loader";
import { useLessonPlanFormOptions } from "./hooks/useLessonPlanFormOptions";
import { handleApiError } from "../../utils/errorHandler";
import {
  createLessonPlan,
  getLessonPlanById,
  updateLessonPlan,
} from "../../services/LessonPlanService";
import LessonPlanForm from "./LessonPlanForm";
import {
  buildLessonPlanPayload,
  getInitialForm,
  getSuccessMessage,
  lessonToForm,
  normalizeLesson,
  validateLessonPlan,
} from "./utils/lessonPlanUtils";
import "./LessonPlanner.css";

const LIST_PATH = "/lesson-planner";

const LessonPlanManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { programData } = useLessonPlanFormOptions();

  // "Create Lesson Plan" from an empty timetable slot pre-fills class, subject and date
  const [form, setForm] = useState(() =>
    getInitialForm({
      program_id: searchParams.get("program_id") || "",
      subject_id: searchParams.get("subject_id") || "",
      lesson_date: searchParams.get("lesson_date") || "",
    }),
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subjectData = useMemo(
    () => programData.find((program) => String(program.id) === String(form.program_id))?.subjects || [],
    [programData, form.program_id],
  );

  /* ================= LOAD (EDIT) ================= */
  const fetchLessonPlan = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await getLessonPlanById(id);
      setForm(lessonToForm(normalizeLesson(response.data.data)));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLessonPlan();
  }, [fetchLessonPlan]);

  /* ================= CHANGE ================= */
  const handleFieldChange = useCallback((name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "program_id" && { subject_id: "" }),
    }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateLessonPlan(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildLessonPlanPayload(form);
      const response = id ? await updateLessonPlan(id, payload) : await createLessonPlan(payload);
      enqueueSnackbar(
        getSuccessMessage(response, id ? "Lesson plan updated" : "Lesson plan created"),
        { variant: "success" },
      );
      navigate(LIST_PATH);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const back = () => navigate(LIST_PATH);

  return (
    <Card componentName={id ? "Update Lesson Plan" : "Create Lesson Plan"} back={back}>
      {isLoading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit} noValidate className="lp-form">
          <LessonPlanForm
            form={form}
            errors={errors}
            programData={programData}
            subjectData={subjectData}
            onFieldChange={handleFieldChange}
          />

          <div className="lp-form-footer">
            <Button variant="secondary" onClick={back} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {id ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
};

export default LessonPlanManage;
